/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { dimensionStyle } from './themes/ChartStyles.css';
import { TrackResizeContainer } from '../PRIVATE_TrackResizeContainer';
import { ChartWithDimensions } from './ChartWithDimensions';
import {
  ChartItemData,
  ChartCommonProps,
  ChartSeriesData,
  GetNextChartItemType,
  Info,
  Scale,
  getDataPosType
} from '../utils/UNSAFE_visTypes/chart';
import { Dimension, MarkerShapes } from '#utils/UNSAFE_visTypes/common';
import { VNode } from 'preact';
import { Property } from 'csstype';

export type ChartProps<K, D> = {
  selectedIds: Set<K>;
  getGapRatio: (startIndex: number, endIndex: number) => number;
  getNavUtil: (startIndex: number, endIndex: number) => GetNextChartItemType;
  getDataItemPos: (
    xScale: Scale,
    yScale: Scale,
    groupWidth: number,
    gapRatio: number
  ) => getDataPosType;
  findNearest: (
    xScale: Scale,
    yScale: Scale,
    groupWidth: number,
    gapRatio: number
  ) => (
    x: number,
    y: number
  ) =>
    | {
        seriesIndex: number;
        groupIndex: number;
        y: number | undefined;
        x: number;
      }
    | undefined;
  children: (context: {
    xStartIndex: number;
    xEndIndex: number;
    xScale: Scale;
    yScale: Scale;
    gapRatio: number;
    activeId?: string;
    focusedItemInfo: Info;
    hoveredItemInfo?: Info;
    groupWidth: number;
    isPointInsideMarquee: ((id: K, point: { x: number; y: number }) => boolean) | undefined;
    getTextDimensions?: (text: string, style: Record<string, any>) => Dimension;
    defaultFontSize: string;
    colors: string[];
  }) => VNode;
  offset: number;
  series: ChartSeriesData<K, D>[];
  isRtl: boolean;
  renderGridLinesInFront: boolean;
  getMarkerInfo: (
    seriesIndex: number,
    groupIndex: number
  ) => { color: Property.Color | undefined; type?: MarkerShapes };
} & Omit<ChartCommonProps<K, D>, 'highlightedIds' | 'hoverBehavior' | 'selectedIds'>;

export function Chart<K extends string | number, D extends ChartItemData<K>>({
  width,
  height,
  ...props
}: ChartProps<K, D>) {
  // TODO: reevaluate how we handle empty data
  return (
    <TrackResizeContainer width={width} height={height} class={dimensionStyle}>
      {(width, height) =>
        width || height ? (
          <ChartWithDimensions width={width} height={height} {...props} />
        ) : undefined
      }
    </TrackResizeContainer>
  );
}
